import { useEffect, useState } from "react";
import type {
  EducationDetailDto,
  EducationType,
  VirtualClassroomDto,
} from "../../../types/client-education-detail.types";
import { resolveEducationDisplayDates } from "../../../utils/education-dates";
import { formatDateRangeLong } from "@/utils/date";
import sectionStyles from "../sections.module.scss";
import { VirtualClassroomCard } from "./VirtualClassroomCard";
import styles from "./VirtualClassrooms.module.scss";

export function VirtualClassroomsSection(props: {
  education: EducationDetailDto;
  /** Eğitim tipi — ON_SITE'da Jitsi katılım butonu yerine konum/tarih bilgisi öne çıkar. */
  educationType?: EducationType;
  now?: Date;
  /** Eğitmen görünümü — kartlar "Dersi Başlat" aksiyonunu gösterir. */
  isInstructor?: boolean;
  onJoin: (vc: VirtualClassroomDto) => void;
}) {
  const {
    education,
    educationType,
    now: nowProp,
    isInstructor = false,
    onJoin,
  } = props;
  // Kart durumları saate bağlı (Başlıyor/CANLI/Bitti): sayfa açıkken buton
  // başlangıç saatinde kendiliğinden aktifleşsin, bitişte kapansın diye
  // 30 sn'de bir tazeleriz. now prop'u verilirse (test vb.) tazeleme yapılmaz.
  const [tick, setTick] = useState(() => new Date());
  useEffect(() => {
    if (nowProp) return;
    const timer = setInterval(() => setTick(new Date()), 30_000);
    return () => clearInterval(timer);
  }, [nowProp]);
  const now = nowProp ?? tick;
  const allClassrooms = education.virtualClassrooms ?? [];
  // Canlı eğitim oluşturulurken backend tarihsiz, saatsiz, üyesiz bir "Canlı
  // Oturum" sınıfı açıyor; Final Planlama'da asıl sınıf kurulunca bu kart
  // "Planlanmamış" etiketiyle yanında boş duruyor ve hangi karttan derse
  // girileceği karışıyordu. Planlanmış en az bir sınıf varsa planlanmamışları
  // gizliyoruz; hiç planlanmış sınıf yoksa bilgi olarak hepsi kalır.
  const isUnscheduled = (vc: VirtualClassroomDto) =>
    !vc.startDate && !vc.endDate && !(vc.scheduledTimes?.length ?? 0);
  const scheduled = allClassrooms.filter((vc) => !isUnscheduled(vc));
  const list = scheduled.length > 0 ? scheduled : allClassrooms;
  if (!list.length) return null;
  // Sınıfların içinde eğitimin başlangıç/bitiş tarihini göster; eğitimin kendi
  // tarihi yoksa sınıf/oturum tarihlerinden türetilir.
  const { start, end } = resolveEducationDisplayDates(education);
  const educationDateText = formatDateRangeLong(start, end) || null;
  return (
    <section id="virtualClassrooms" className={sectionStyles.section}>
      <div className={sectionStyles.header}><h2>Planlanan Oturumlar</h2></div>
      <div className={styles.grid}>
        {list.map((vc) => (
          <VirtualClassroomCard
            key={vc.id}
            vc={vc}
            now={now}
            educationType={educationType ?? (education as { type?: EducationType }).type}
            educationDateText={educationDateText}
            isInstructor={isInstructor}
            onJoin={onJoin}
          />
        ))}
      </div>
    </section>
  );
}
